import { useState, useEffect, useCallback, useRef } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import { handleApiError } from "@/services/api";
import type { BulkSyncResult } from "@/types/bulkSync";
import {
  flockService,
  feedFormulaService,
  rawMaterialService,
  buyerService,
} from "@/services/masterDataService";
import {
  feedLogService,
  eggProductionService,
  mortalityLogService,
} from "@/services/dailyLoggingService";
import { feedProductionService } from "@/services/feedProductionService";
import {
  flockMetricsService,
  toMeasure,
  type SaveFlockMetrics,
} from "@/services/flockMetricsService";
import { stockService } from "@/services/stockService";
import { eggSaleService } from "@/services/eggSaleService";
import { birdSaleService } from "@/services/birdSaleService";
import { manureSaleService } from "@/services/manureSaleService";
import { bagSaleService } from "@/services/bagSaleService";
import type { PaymentDraft } from "@/hooks/useSalePayments";
import { paidNow, paidLines, takesPayment } from "./paidNow";
import { useIsManager } from "@/hooks/useRole";
import type { Flock, FeedFormula, Buyer, MortalityLog } from "@/types/models";
import StepDate from "./StepDate";
import StepFlockData from "./StepFlockData";
import { EGGS_PER_TRAY, flockDataProblem } from "./flockDataCheck";
import StepMeasurements, { hasAnyMeasure } from "./StepMeasurements";
import StepFeedPrepared from "./StepFeedPrepared";
import StepRawMaterials from "./StepRawMaterials";
import StepSales from "./StepSales";
import { ADHOC_BUYER } from "./constants";
import StepReview from "./StepReview";

export interface FlockEntry {
  flock_id: number;
  batch_name: string;
  flock_type: "layer" | "brooder";
  shed_type: "layer" | "brooder";
  shed_code: string;
  current_count: number;
  // Flock Data step
  mortality: string;
  /** "" = not chosen: the cause already logged (if any) is kept, not cleared. */
  mortality_cause: string;
  egg_trays: string;
  broken_trays: string;
  feed_consumed_kg: string;
  // Feed Prepared step
  feed_prepared_kg: string;
  formula_id: string;
  // Measurements step. Prefixed `m_` so they're recognisable as one group in
  // the flat entry, and kept as raw strings like everything else here — the
  // distinction that matters (blank means NOT MEASURED, which is not zero)
  // survives as "" and is converted by toMeasure() at submit, never by
  // Number(), which turns "" into 0.
  m_body_weight_g: string;
  m_egg_weight_g: string;
  m_water_litres: string;
  m_light_hours: string;
  m_temp_min_c: string;
  m_temp_max_c: string;
  m_humidity_pct: string;
  // Prefilled and never SHOWN. The wizard has no notes box, but the upsert
  // assigns `notes = EXCLUDED.notes` like every other column, so sending null
  // would delete a note written from the Measurements page — silently, with
  // nothing on screen to suggest a note existed. Carried, not displayed.
  m_notes: string;
  // What the day already had when the wizard loaded it, never shown. A
  // re-submit writes a CORRECTION against these: deaths changed to 0 must
  // clear the logged deaths (the bulk write skips a 0 otherwise), and eggs
  // whose trays the supervisor didn't touch must not be rewritten from them —
  // 935 eggs logged on the Eggs page pre-fill as 31 trays, and 31 × 30 would
  // silently drop the 5 loose ones.
  prior_mortality: number | null;
  prior_egg: { trays: string; total_trays: number; total_eggs: number } | null;
}

/** No day loaded yet. */
const noPrior = () => ({ prior_mortality: null, prior_egg: null });

/** The measurement fields, blanked. Used at load, on every date change and on
 *  reset — three places that must agree, so they share one definition. */
const blankMeasures = () => ({
  m_body_weight_g: "",
  m_egg_weight_g: "",
  m_water_litres: "",
  m_light_hours: "",
  m_temp_min_c: "",
  m_temp_max_c: "",
  m_humidity_pct: "",
  m_notes: "",
});

/** A stored measure back into a form value: null (not measured) is a blank
 *  box, not a zero. */
const measureStr = (v: number | null | undefined): string =>
  v === null || v === undefined ? "" : String(v);

export interface MaterialEntry {
  raw_material_id: number;
  name: string;
  unit: string;
  qty: string;
}

// Sales entries. All fields are strings (raw input) until submit;
// buyer_id of "" = unselected, ADHOC_BUYER = walk-in (manure + bag only).
export interface EggSaleEntry {
  buyer_id: string;
  qty_eggs: string;
  price_per_egg: string;
  total_amount: string;
  broken_eggs: string;
  notes: string;
  /** Paid at the time of sale (W2-6); none means the whole sale is on credit. */
  payments?: PaymentDraft[];
}

export interface BirdSaleEntry {
  buyer_id: string;
  flock_id: string;
  bird_count: string;
  total_weight_kg: string;
  rate_type: "per_kg" | "per_bird";
  rate: string;
  total_amount: string;
  notes: string;
  /** Paid at the time of sale (W2-6); none means the whole sale is on credit. */
  payments?: PaymentDraft[];
}

export interface ManureSaleEntry {
  buyer_id: string; // master id OR ADHOC_BUYER sentinel
  buyer_adhoc: string; // free-text used only when buyer_id === ADHOC_BUYER
  qty: string;
  unit: string;
  rate_per_unit: string;
  total_amount: string;
  notes: string;
  /** Paid at the time of sale (W2-6); none means the whole sale is on credit. */
  payments?: PaymentDraft[];
}

export interface BagSaleEntry {
  buyer_id: string;
  buyer_adhoc: string;
  bag_type: string;
  qty: string;
  rate_per_unit: string;
  total_amount: string;
  notes: string;
  /** Paid at the time of sale (W2-6); none means the whole sale is on credit. */
  payments?: PaymentDraft[];
}

export interface SalesData {
  eggs: EggSaleEntry[];
  birds: BirdSaleEntry[];
  manure: ManureSaleEntry[];
  bags: BagSaleEntry[];
}

export interface DailyReportData {
  date: string;
  flocks: FlockEntry[];
  materials: MaterialEntry[];
  sales: SalesData;
}

// Step labels, in the order the round actually happens. Measurements sits
// immediately after Flock Data because it is the SAME shed visit — the sample
// is weighed and the thermometer read while the operator is standing there —
// whereas Feed Prepared and Raw Materials happen at the mill and the gate.
//
// These are named rather than numbered because the step indices used to be
// hard-coded integers (`step === 2`, `SALES_STEP_INDEX = 4`), and inserting a
// step into that is exactly where an off-by-one hides: the Feed Prepared
// validation would have started guarding the Raw Materials screen, silently.
const STEP_DATE = "Date";
const STEP_FLOCK = "Flock Data";
const STEP_MEASUREMENTS = "Measurements";
const STEP_FEED = "Feed Prepared";
const STEP_MATERIALS = "Raw Materials";
const STEP_SALES = "Sales";
const STEP_REVIEW = "Review";

export interface SubmitResult {
  label: string;
  /** "warning": saved, but the server flagged something (feed produced past the stock on hand). */
  status: "success" | "warning" | "error";
  message: string;
}

/**
 * Report what a bulk write ACTUALLY did, not what was sent.
 *
 * A bulk endpoint answers 200 with per-item results: items can be refused
 * individually (a date that will not parse, a sale the credit gate blocks, a
 * row the database rejects) while the rest are written. Reporting
 * `entries.length` on any non-throwing response tells the operator that five
 * sheds were logged when four were — on the screen they are about to leave,
 * which is the last moment it can be corrected.
 *
 * Failures are named per item where the caller can name them, because
 * "2 of 5 failed" is not something anyone can act on and "Shed B: date is
 * required" is. Same rule the Measurements step already follows.
 */
function reportBulk<T>(
  outcomes: SubmitResult[],
  label: string,
  res: BulkSyncResult<T>,
  describe: (n: number) => string,
  nameOf?: (index: number) => string | undefined,
) {
  // res.success_count, with no fallback to what was sent.
  //
  // An earlier draft had `?? sent`, justified as tolerating an endpoint that
  // answers without the envelope. No such endpoint exists — the only thing it
  // tolerated was nine stale test mocks returning a bare array, which is how
  // it came to prop up the very assertions meant to check this. Falling back
  // to the request length is the UI guessing rather than admitting ignorance:
  // the same move the backend change removed.
  const ok = res.success_count;
  if (ok > 0) {
    outcomes.push({ label, status: "success", message: describe(ok) });
  }
  // `failed` is null-able on the wire (Go marshals a nil slice as null), but
  // `res` itself is not optional — an endpoint that answers without the
  // envelope is exactly what this function refuses to paper over.
  for (const fail of res.failed ?? []) {
    // index -1 is the server saying it could not map the failure back to a
    // request. Naming a row from it would invent one.
    const who = fail.index >= 0 ? nameOf?.(fail.index) : undefined;
    const where = fail.index >= 0 ? `row ${fail.index + 1}` : "unmatched row";
    outcomes.push({
      label: who ? `${label} - ${who}` : `${label} (${where})`,
      status: "error",
      message: fail.error,
    });
  }
}

const emptySales = (): SalesData => ({ eggs: [], birds: [], manure: [], bags: [] });

const emptyEggRow = (): EggSaleEntry => ({
  buyer_id: "",
  qty_eggs: "",
  price_per_egg: "",
  total_amount: "",
  broken_eggs: "0",
  notes: "",
});
const emptyBirdRow = (): BirdSaleEntry => ({
  buyer_id: "",
  flock_id: "",
  bird_count: "",
  total_weight_kg: "",
  rate_type: "per_kg",
  rate: "",
  total_amount: "",
  notes: "",
});
const emptyManureRow = (): ManureSaleEntry => ({
  buyer_id: "",
  buyer_adhoc: "",
  qty: "",
  unit: "load",
  rate_per_unit: "",
  total_amount: "",
  notes: "",
});
const emptyBagRow = (): BagSaleEntry => ({
  buyer_id: "",
  buyer_adhoc: "",
  bag_type: "plastic",
  qty: "",
  rate_per_unit: "",
  total_amount: "",
  notes: "",
});

// Edit window: today + the previous EDIT_WINDOW_DAYS-1 days. Anything older
// is owner-only — the wizard shows a block message and won't let the
// supervisor/manager proceed. Future dates are still rejected.
//
// History: 3 → 15 for weekly review / short leave / month-end catch-up.
//
// Now 1000 (≈2.7 years), which effectively disables the window. This is a
// DELIBERATE TEMPORARY WIDENING: a production client's data was cleared and
// their staff need to re-enter historical entries through this wizard, which
// the 15-day gate blocked. Widening the constant was chosen over deleting the
// mechanism so it can be narrowed again by editing one line.
//
// Follow-up (not done here): make this per-farm via the `farms.features`
// JSONB — same pattern as `daily_report_mode` — so the
// window can be widened for one farm from settings instead of globally by
// redeploy. Note also that this gate is CLIENT-SIDE ONLY; the backend does
// not validate log dates, so "owner-only" is a UI guard, not a permission
// boundary. Trade-off while wide: dashboard KPIs (HDP, FCR, weekly mortality
// rate) can shift retroactively far further back than two weeks.
const EDIT_WINDOW_DAYS = 1000;

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dateStrOffset(daysBack: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysBack);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Calendar-day distance from today, computed in local time so that the
// HTML date input's YYYY-MM-DD value (which has no TZ) compares against
// the user's local "today" rather than UTC midnight.
function daysBackFromToday(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return NaN;
  const selected = new Date(y, m - 1, d).getTime();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((today - selected) / (1000 * 60 * 60 * 24));
}

export default function DailyReportPage() {
  const isManager = useIsManager();
  const STEPS = isManager
    ? [STEP_DATE, STEP_FLOCK, STEP_MEASUREMENTS, STEP_FEED, STEP_MATERIALS, STEP_SALES, STEP_REVIEW]
    : [STEP_DATE, STEP_FLOCK, STEP_MEASUREMENTS, STEP_FEED, STEP_MATERIALS, STEP_REVIEW];
  // Every step is addressed by name. `at(STEP_SALES)` is -1 for a supervisor,
  // which is the "never matches" sentinel the sales screen already relied on.
  const at = (label: string) => STEPS.indexOf(label);
  const SALES_STEP_INDEX = at(STEP_SALES);
  const REVIEW_STEP_INDEX = STEPS.length - 1;

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState<SubmitResult[]>([]);
  // Pre-fill machinery — see handleDateChange for the full flow.
  const [prefilling, setPrefilling] = useState(false);
  const [existingCount, setExistingCount] = useState(0);
  // Tracked separately from `existingCount`, which gates `editMode` and with it
  // the Feed Prepared validation. A day on which only the thermometer was read
  // is not a day already entered — folding measurements into that count would
  // quietly switch off a required field on a screen nobody was looking at.
  const [measuresPrefilled, setMeasuresPrefilled] = useState(false);
  // Could the day's existing readings be READ? If not, the boxes are blank for
  // a reason that has nothing to do with the farm, and writing over them would
  // erase whatever is actually stored. The step goes read-only rather than
  // presenting a blank form it cannot honour.
  const [measuresLoadFailed, setMeasuresLoadFailed] = useState(false);
  // Same question for the other three, which cannot lock themselves: feed and
  // egg trays are REQUIRED to get past the Flock Data step, so the operator
  // has to type something. Their writes are upserts too — the egg bulk write
  // assigns `broken_eggs` and `notes` from the submission — so a blank that
  // means "couldn't read" and gets retyped over silently replaces what was
  // stored. It can only be said out loud, on the Date step.
  const [logsLoadFailed, setLogsLoadFailed] = useState(false);
  const [dateError, setDateError] = useState("");

  const [flocks, setFlocks] = useState<Flock[]>([]);
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);
  const [buyers, setBuyers] = useState<Buyer[]>([]);

  const [data, setData] = useState<DailyReportData>({
    date: todayStr(),
    flocks: [],
    materials: [],
    sales: emptySales(),
  });

  // Whether the day's entries are already in the current flock rows; reset
  // whenever the rows are rebuilt below.
  const prefillDoneOnce = useRef(false);
  useEffect(() => {
    // Ignore a superseded run: under StrictMode a first run resolving after
    // the pre-fill rebuilt the rows blank under "Showing what's already saved".
    let superseded = false;
    const fetchBuyers = isManager
      ? buyerService.list()
      : Promise.resolve([] as Buyer[]);
    Promise.all([
      flockService.list(),
      feedFormulaService.list(),
      rawMaterialService.list(),
      fetchBuyers,
    ])
      .then(([allFlocks, allFormulas, allMaterials, allBuyers]) => {
        if (superseded) return;
        // Fresh rows need the day's entries again — reset in the same batch
        // as the rebuild, so no render can see new rows with the old flag.
        prefillDoneOnce.current = false;
        const active = allFlocks.filter((f) => f.status === "active");
        const activeFormulas = allFormulas.filter((f) => f.active);
        setFlocks(active);
        setFormulas(activeFormulas);
        setBuyers(allBuyers as Buyer[]);

        setData((prev) => ({
          ...prev,
          flocks: active.map((f) => ({
            flock_id: f.id,
            batch_name: f.batch_name,
            flock_type: f.flock_type,
            shed_type: f.shed_type || f.flock_type,
            shed_code: f.shed_code || "",
            current_count: f.current_count,
            mortality: "0",
            mortality_cause: "",
            egg_trays: "",
            broken_trays: "0",
            feed_consumed_kg: "",
            feed_prepared_kg: "",
            formula_id: f.default_formula_id?.toString() || "",
            ...blankMeasures(),
            ...noPrior(),
          })),
          materials: allMaterials.map((m) => ({
            raw_material_id: m.id,
            name: m.name,
            unit: m.unit,
            qty: "",
          })),
        }));
      })
      .catch(() => {})
      .finally(() => {
        if (!superseded) setLoading(false);
      });
    return () => {
      superseded = true;
    };
  }, [isManager]);

  // Race guard for handleDateChange: if the user changes the date again
  // while a previous fetch is still in flight, we want the newer call's
  // pre-fill to win — not whichever one's network round-trip lands second.
  // `latestDateRef.current` is mutated on every entry; only the call whose
  // captured `newDate` still matches at await-resume time commits state.
  const latestDateRef = useRef<string>("");

  // When the date changes we (a) reset per-flock daily-log fields to
  // defaults so stale pre-fill from a previously-selected date doesn't
  // leak through, (b) enforce the edit window (EDIT_WINDOW_DAYS), and (c) fetch
  // existing logs for the new date to pre-fill them.
  //
  // Scope note: only the per-flock daily-log fields (mortality, eggs,
  // feed consumed) are pre-filled. Feed production (step 3) and raw
  // material stock-in (step 4) are not pre-filled — they represent
  // point-in-time events (a batch produced, a delivery received), not
  // daily totals. If those need editing, the owner uses the regular
  // per-resource pages.
  const handleDateChange = useCallback(async (newDate: string) => {
    latestDateRef.current = newDate;
    setData((prev) => ({
      ...prev,
      date: newDate,
      flocks: prev.flocks.map((f) => ({
        ...f,
        mortality: "0",
        mortality_cause: "",
        egg_trays: "",
        broken_trays: "0",
        feed_consumed_kg: "",
        feed_prepared_kg: "",
        formula_id:
          flocks.find((fl) => fl.id === f.flock_id)?.default_formula_id?.toString() || "",
        ...blankMeasures(),
        ...noPrior(),
      })),
    }));
    setExistingCount(0);
    setMeasuresPrefilled(false);
    setMeasuresLoadFailed(false);
    setLogsLoadFailed(false);

    const daysBack = daysBackFromToday(newDate);
    if (isNaN(daysBack)) {
      setDateError("");
      return;
    }
    if (daysBack < 0) {
      setDateError("You can't log entries for a future date.");
      return;
    }
    if (daysBack >= EDIT_WINDOW_DAYS) {
      setDateError(
        `Entries for ${newDate} are older than ${EDIT_WINDOW_DAYS} days and can only be edited by the owner.`,
      );
      return;
    }
    setDateError("");

    setPrefilling(true);
    try {
      // allSettled, not all. Four independent resources: a 500 on one must not
      // blank the other three's pre-fill, which is what `Promise.all` did.
      //
      // It matters most for the fourth. Measurements must be pre-filled, not
      // merely offered blank — the write is an upsert that assigns EVERY
      // column, so submitting a body weight alone onto a day that already had
      // water logged would clear the water. A FAILED load looks exactly like
      // "nothing measured yet", so the step has to be told the difference and
      // refuse to write over what it could not read.
      const [feedSettled, eggSettled, mortSettled, metricSettled] =
        await Promise.allSettled([
          feedLogService.list({ from: newDate, to: newDate, limit: 200 }),
          eggProductionService.list({ from: newDate, to: newDate, limit: 200 }),
          mortalityLogService.list({ from: newDate, to: newDate, limit: 200 }),
          flockMetricsService.list({ from: newDate, to: newDate, limit: 200 }),
        ]);
      const emptyPage = { data: [] as never[] };
      const feedRes = feedSettled.status === "fulfilled" ? feedSettled.value : emptyPage;
      const eggRes = eggSettled.status === "fulfilled" ? eggSettled.value : emptyPage;
      const mortRes = mortSettled.status === "fulfilled" ? mortSettled.value : emptyPage;
      const metricRows = metricSettled.status === "fulfilled" ? metricSettled.value : [];
      const metricsFailed = metricSettled.status === "rejected";
      const logsFailed =
        feedSettled.status === "rejected" ||
        eggSettled.status === "rejected" ||
        mortSettled.status === "rejected";
      // Bail if the user moved on — a later call now owns the UI state.
      if (latestDateRef.current !== newDate) return;

      const feedByFlock = new Map(feedRes.data.map((r) => [r.flock_id, r]));
      const eggByFlock = new Map(eggRes.data.map((r) => [r.flock_id, r]));
      const mortByFlock = new Map(mortRes.data.map((r) => [r.flock_id, r]));
      const metricsByFlock = new Map(metricRows.map((r) => [r.flock_id, r]));
      setMeasuresPrefilled(metricRows.length > 0);
      setMeasuresLoadFailed(metricsFailed);
      setLogsLoadFailed(logsFailed);

      // Count distinct flocks with any pre-filled value, not raw rows.
      // "15 entries logged" reads to a 5-flock farm as "we have 5 flocks
      // and 15 things, what?" — flock-count maps to what they actually see.
      const flocksWithEntries = new Set<number>();
      feedRes.data.forEach((r) => flocksWithEntries.add(r.flock_id));
      eggRes.data.forEach((r) => flocksWithEntries.add(r.flock_id));
      mortRes.data.forEach((r) => flocksWithEntries.add(r.flock_id));
      setExistingCount(flocksWithEntries.size);

      setData((prev) => ({
        ...prev,
        flocks: prev.flocks.map((f) => {
          const feed = feedByFlock.get(f.flock_id);
          const egg = eggByFlock.get(f.flock_id);
          const mort = mortByFlock.get(f.flock_id);
          const met = metricsByFlock.get(f.flock_id);
          return {
            ...f,
            mortality: mort ? String(mort.count) : "0",
            mortality_cause: mort?.cause ?? "",
            egg_trays: egg ? String(egg.total_trays) : "",
            broken_trays: egg ? String(egg.broken_eggs ?? 0) : "0",
            feed_consumed_kg: feed ? String(feed.qty_kg) : "",
            formula_id: feed?.formula_id
              ? String(feed.formula_id)
              : f.formula_id,
            m_body_weight_g: measureStr(met?.avg_body_weight_g),
            m_egg_weight_g: measureStr(met?.avg_egg_weight_g),
            m_water_litres: measureStr(met?.water_litres),
            m_light_hours: measureStr(met?.light_hours),
            m_temp_min_c: measureStr(met?.temp_min_c),
            m_temp_max_c: measureStr(met?.temp_max_c),
            m_humidity_pct: measureStr(met?.humidity_pct),
            m_notes: met?.notes ?? "",
            prior_mortality: mort ? mort.count : null,
            prior_egg: egg
              ? {
                  trays: String(egg.total_trays),
                  total_trays: egg.total_trays,
                  total_eggs: egg.total_eggs,
                }
              : null,
          };
        }),
      }));
    } catch {
      // Pre-fill is best-effort for the three daily logs: a transient failure
      // costs the operator some retyping, nothing more. It is NOT best-effort
      // for measurements — see setMeasuresLoadFailed above — so anything that
      // escapes the per-resource handling locks that step rather than letting
      // it overwrite a day it never read.
      setMeasuresLoadFailed(true);
      setLogsLoadFailed(true);
    } finally {
      // Only the latest in-flight call may clear the spinner — otherwise an
      // older call's finally would flicker prefilling off while the newer
      // one is still loading.
      if (latestDateRef.current === newDate) {
        setPrefilling(false);
      }
    }
  }, [flocks]);

  // Pre-fill today's existing entries (if any) once master data lands.
  // The ref (declared above, beside the master-data load that resets it)
  // ensures this only fires once per set of flock rows, regardless of how
  // often the deps technically re-evaluate.
  useEffect(() => {
    if (!loading && flocks.length > 0 && !prefillDoneOnce.current) {
      prefillDoneOnce.current = true;
      void handleDateChange(todayStr());
    }
  }, [loading, flocks.length, handleDateChange]);

  const updateFlock = useCallback((index: number, field: keyof FlockEntry, value: string) => {
    setData((prev) => {
      const flocks = [...prev.flocks];
      flocks[index] = { ...flocks[index], [field]: value };
      return { ...prev, flocks };
    });
  }, []);

  const updateMaterial = useCallback((index: number, qty: string) => {
    setData((prev) => {
      const materials = [...prev.materials];
      materials[index] = { ...materials[index], qty };
      return { ...prev, materials };
    });
  }, []);

  // Sales row mutators — generic helpers per type.
  const addEgg = useCallback(() => {
    setData((prev) => ({
      ...prev,
      sales: { ...prev.sales, eggs: [...prev.sales.eggs, emptyEggRow()] },
    }));
  }, []);
  const updateEgg = useCallback((index: number, patch: Partial<EggSaleEntry>) => {
    setData((prev) => {
      const eggs = [...prev.sales.eggs];
      eggs[index] = { ...eggs[index], ...patch };
      return { ...prev, sales: { ...prev.sales, eggs } };
    });
  }, []);
  const removeEgg = useCallback((index: number) => {
    setData((prev) => ({
      ...prev,
      sales: { ...prev.sales, eggs: prev.sales.eggs.filter((_, i) => i !== index) },
    }));
  }, []);

  const addBird = useCallback(() => {
    setData((prev) => ({
      ...prev,
      sales: { ...prev.sales, birds: [...prev.sales.birds, emptyBirdRow()] },
    }));
  }, []);
  const updateBird = useCallback((index: number, patch: Partial<BirdSaleEntry>) => {
    setData((prev) => {
      const birds = [...prev.sales.birds];
      birds[index] = { ...birds[index], ...patch };
      return { ...prev, sales: { ...prev.sales, birds } };
    });
  }, []);
  const removeBird = useCallback((index: number) => {
    setData((prev) => ({
      ...prev,
      sales: { ...prev.sales, birds: prev.sales.birds.filter((_, i) => i !== index) },
    }));
  }, []);

  const addManure = useCallback(() => {
    setData((prev) => ({
      ...prev,
      sales: { ...prev.sales, manure: [...prev.sales.manure, emptyManureRow()] },
    }));
  }, []);
  const updateManure = useCallback((index: number, patch: Partial<ManureSaleEntry>) => {
    setData((prev) => {
      const manure = [...prev.sales.manure];
      manure[index] = { ...manure[index], ...patch };
      return { ...prev, sales: { ...prev.sales, manure } };
    });
  }, []);
  const removeManure = useCallback((index: number) => {
    setData((prev) => ({
      ...prev,
      sales: { ...prev.sales, manure: prev.sales.manure.filter((_, i) => i !== index) },
    }));
  }, []);

  const addBag = useCallback(() => {
    setData((prev) => ({
      ...prev,
      sales: { ...prev.sales, bags: [...prev.sales.bags, emptyBagRow()] },
    }));
  }, []);
  const updateBag = useCallback((index: number, patch: Partial<BagSaleEntry>) => {
    setData((prev) => {
      const bags = [...prev.sales.bags];
      bags[index] = { ...bags[index], ...patch };
      return { ...prev, sales: { ...prev.sales, bags } };
    });
  }, []);
  const removeBag = useCallback((index: number) => {
    setData((prev) => ({
      ...prev,
      sales: { ...prev.sales, bags: prev.sales.bags.filter((_, i) => i !== index) },
    }));
  }, []);

  // "0" is truthy in JS, so a bare !r.x check would pass an explicit zero —
  // backend then rejects with gt=0. Require positive numbers explicitly.
  const positive = (s: string) => parseFloat(s) > 0;

  const validateSalesStep = (): boolean => {
    // Paid now can't exceed the sale — the server refuses it, and the box says so.
    const all = [...data.sales.eggs, ...data.sales.birds, ...data.sales.manure, ...data.sales.bags];
    if (all.some((r) => takesPayment(r) && paidNow(r).exceedsTotal)) return false;
    // Empty sections are fine — operator can skip. Any added row must be complete.
    for (const r of data.sales.eggs) {
      if (!r.buyer_id) return false;
      if (!positive(r.qty_eggs) || !positive(r.price_per_egg) || !positive(r.total_amount)) return false;
    }
    for (const r of data.sales.birds) {
      if (!r.buyer_id) return false;
      if (!positive(r.bird_count) || !positive(r.rate) || !positive(r.total_amount)) return false;
    }
    for (const r of data.sales.manure) {
      if (!r.buyer_id || !r.unit) return false;
      if (!positive(r.qty) || !positive(r.rate_per_unit) || !positive(r.total_amount)) return false;
      if (r.buyer_id === ADHOC_BUYER && !r.buyer_adhoc.trim()) return false;
    }
    for (const r of data.sales.bags) {
      if (!r.buyer_id || !r.bag_type) return false;
      if (!positive(r.qty) || !positive(r.rate_per_unit) || !positive(r.total_amount)) return false;
      if (r.buyer_id === ADHOC_BUYER && !r.buyer_adhoc.trim()) return false;
    }
    return true;
  };

  const canProceed = (): boolean => {
    const editMode = existingCount > 0;
    if (step === at(STEP_DATE)) return !!data.date && !dateError && !prefilling;
    if (step === at(STEP_FLOCK)) {
      return data.flocks.every((f) => {
        // An impossible entry blocks Next on EVERY flock — including an empty
        // one being back-filled — and the card says why.
        if (flockDataProblem(f)) return false;
        // A flock with no birds left (wiped out by deaths stays ACTIVE until
        // the owner closes it, by design) has nothing to report: requiring
        // feed and trays for an empty shed made the supervisor type numbers
        // to get past the step. Its card stays editable for a back-dated day.
        if (f.current_count <= 0) return true;
        if (!f.feed_consumed_kg) return false;
        if (f.shed_type === "layer" && !f.egg_trays) return false;
        return true;
      });
    }
    // Measurements requires nothing. Body weight is a weekly sample on most
    // farms, so a step that blocked Next would be a daily tax for a weekly job
    // — and the first thing anyone would do is type a number to get past it.
    if (step === at(STEP_FEED)) {
      // Edit mode: feed production isn't pre-filled (point-in-time
      // batch, not a daily total) and the operator may just be
      // correcting mortality/eggs. Don't block Next on blank fields —
      // the submit path already filters out empty values, so blank
      // here means "no new feed-production batch for this date".
      if (editMode) return true;
      // Feed Prepared is optional day-to-day (only on batch-mixing days). Require
      // a formula ONLY when a positive amount was prepared; blank/0 means "no
      // batch today" and must not block. Submit already filters to kg>0 && formula.
      return data.flocks.every((f) => {
        const kg = parseFloat(f.feed_prepared_kg) || 0;
        return kg <= 0 || !!f.formula_id;
      });
    }
    if (step === SALES_STEP_INDEX) return validateSalesStep();
    return true;
  };

  const buyerName = (id: string) => {
    if (id === ADHOC_BUYER) return "walk-in";
    return buyers.find((b) => b.id === parseInt(id))?.name || `buyer #${id}`;
  };

  // Names for the per-item failures a bulk write can report. The index is
  // into the array the CLIENT sent, which is what the backend's BulkItemError
  // promises — it maps its own indices back before answering, precisely so
  // this lookup is possible.
  const flockName = (flockID: number | undefined) =>
    data.flocks.find((f) => f.flock_id === flockID)?.batch_name;
  const materialName = (materialID: number | undefined) =>
    data.materials.find((m) => m.raw_material_id === materialID)?.name;

  const paymentLines = (r: { buyer_id: string; total_amount: string; payments?: PaymentDraft[] }) => {
    const lines = paidLines(r);
    return lines.length ? lines : undefined;
  };
  const paidNote = (r: { buyer_id: string; total_amount: string; payments?: PaymentDraft[] }) => {
    const paid = paidLines(r).reduce((sum, l) => sum + l.amount, 0);
    return paid > 0 ? `, ₹${paid.toLocaleString("en-IN", { maximumFractionDigits: 2 })} paid` : "";
  };

  const submitSales = async (outcomes: SubmitResult[]) => {
    // Egg sales
    for (const r of data.sales.eggs) {
      const label = `Egg Sale (${buyerName(r.buyer_id)})`;
      try {
        await eggSaleService.create({
          date: data.date,
          qty_eggs: parseInt(r.qty_eggs),
          price_per_egg: parseFloat(r.price_per_egg),
          total_amount: Math.round(parseFloat(r.total_amount)),
          broken_eggs: parseInt(r.broken_eggs) || 0,
          buyer_id: parseInt(r.buyer_id),
          notes: r.notes || undefined,
          payments: paymentLines(r),
        });
        outcomes.push({
          label,
          status: "success",
          message: `${r.qty_eggs} eggs / ₹${Math.round(parseFloat(r.total_amount))}${paidNote(r)}`,
        });
      } catch (err) {
        outcomes.push({ label, status: "error", message: handleApiError(err) });
      }
    }
    // Bird sales
    for (const r of data.sales.birds) {
      const label = `Bird Sale (${buyerName(r.buyer_id)})`;
      try {
        const ratePerKg = r.rate_type === "per_kg" ? parseFloat(r.rate) : undefined;
        const ratePerBird = r.rate_type === "per_bird" ? parseFloat(r.rate) : undefined;
        await birdSaleService.create({
          date: data.date,
          flock_id: r.flock_id ? parseInt(r.flock_id) : undefined,
          bird_count: parseInt(r.bird_count),
          total_weight_kg: r.total_weight_kg ? parseFloat(r.total_weight_kg) : undefined,
          rate_per_kg: ratePerKg,
          rate_per_bird: ratePerBird,
          total_amount: Math.round(parseFloat(r.total_amount)),
          buyer_id: parseInt(r.buyer_id),
          notes: r.notes || undefined,
          payments: paymentLines(r),
        });
        outcomes.push({
          label,
          status: "success",
          message: `${r.bird_count} birds / ₹${Math.round(parseFloat(r.total_amount))}${paidNote(r)}`,
        });
      } catch (err) {
        outcomes.push({ label, status: "error", message: handleApiError(err) });
      }
    }
    // Manure sales
    for (const r of data.sales.manure) {
      const label = `Manure Sale (${buyerName(r.buyer_id)})`;
      try {
        const isAdhoc = r.buyer_id === ADHOC_BUYER;
        await manureSaleService.create({
          date: data.date,
          qty: parseFloat(r.qty),
          unit: r.unit,
          rate_per_unit: parseFloat(r.rate_per_unit),
          total_amount: Math.round(parseFloat(r.total_amount)),
          buyer_id: isAdhoc ? undefined : parseInt(r.buyer_id),
          buyer: isAdhoc ? r.buyer_adhoc.trim() : undefined,
          notes: r.notes || undefined,
          payments: paymentLines(r),
        });
        outcomes.push({
          label,
          status: "success",
          message: `${r.qty} ${r.unit} / ₹${Math.round(parseFloat(r.total_amount))}${paidNote(r)}`,
        });
      } catch (err) {
        outcomes.push({ label, status: "error", message: handleApiError(err) });
      }
    }
    // Bag sales
    for (const r of data.sales.bags) {
      const label = `Bag Sale (${buyerName(r.buyer_id)})`;
      try {
        const isAdhoc = r.buyer_id === ADHOC_BUYER;
        await bagSaleService.create({
          date: data.date,
          bag_type: r.bag_type,
          qty: parseInt(r.qty),
          rate_per_unit: parseFloat(r.rate_per_unit),
          total_amount: Math.round(parseFloat(r.total_amount)),
          buyer_id: isAdhoc ? undefined : parseInt(r.buyer_id),
          buyer: isAdhoc ? r.buyer_adhoc.trim() : undefined,
          notes: r.notes || undefined,
          payments: paymentLines(r),
        });
        outcomes.push({
          label,
          status: "success",
          message: `${r.qty} ${r.bag_type} / ₹${Math.round(parseFloat(r.total_amount))}${paidNote(r)}`,
        });
      } catch (err) {
        outcomes.push({ label, status: "error", message: handleApiError(err) });
      }
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const outcomes: SubmitResult[] = [];

    // 1. Mortality: flocks with deaths, and flocks whose logged deaths were
    // corrected to 0 — the server clears that day's row and gives the birds
    // back. A 0 on a day with nothing logged is nothing to write.
    const mortalityEntries = data.flocks
      .filter((f) => (parseInt(f.mortality) || 0) > 0 || (f.prior_mortality ?? 0) > 0)
      .map((f) => ({
        flock_id: f.flock_id,
        date: data.date,
        count: Math.max(parseInt(f.mortality) || 0, 0),
        // The cause only when one was chosen (live walk 2, W2-5: the wizard
        // had no field, so every supervisor's death was saved with no cause
        // and "mortality by cause" could not split them). None chosen is
        // NOT sent: the upsert keeps a cause already logged on the Mortality
        // page rather than clearing it.
        ...(f.mortality_cause ? { cause: f.mortality_cause as MortalityLog["cause"] } : {}),
      }));

    if (mortalityEntries.length > 0) {
      try {
        const res = await mortalityLogService.bulkCreate(mortalityEntries);
        reportBulk(outcomes, "Mortality", res,
          (n) => `${n} ${n === 1 ? "entry" : "entries"} recorded`,
          (i) => flockName(mortalityEntries[i]?.flock_id));
      } catch (err) {
        outcomes.push({ label: "Mortality", status: "error", message: handleApiError(err) });
      }
    }

    // 2. Egg production (layer flocks only). Trays left as pre-filled send the
    // day's stored egg count, not trays × 30 (see prior_egg). The server's
    // trays are whole trays — a half tray is in the egg count, and sending
    // 30.5 trays was refused.
    const eggEntries = data.flocks
      .filter((f) => f.shed_type === "layer" && f.egg_trays)
      .map((f) => {
        const broken = parseFloat(f.broken_trays) || 0;
        const kept = f.prior_egg !== null && f.prior_egg.trays === f.egg_trays ? f.prior_egg : null;
        const eggs = kept
          ? kept.total_eggs
          : Math.round((parseFloat(f.egg_trays) || 0) * EGGS_PER_TRAY);
        return {
          flock_id: f.flock_id,
          date: data.date,
          total_trays: kept ? kept.total_trays : Math.floor(eggs / EGGS_PER_TRAY),
          total_eggs: eggs,
          broken_eggs: Math.round(broken),
        };
      });

    if (eggEntries.length > 0) {
      try {
        const res = await eggProductionService.bulkCreate(eggEntries);
        reportBulk(outcomes, "Egg Production", res,
          (n) => `${n} ${n === 1 ? "entry" : "entries"} recorded`,
          (i) => flockName(eggEntries[i]?.flock_id));
      } catch (err) {
        outcomes.push({ label: "Egg Production", status: "error", message: handleApiError(err) });
      }
    }

    // 3. Feed distribution (consumption)
    const feedEntries = data.flocks
      .filter((f) => f.feed_consumed_kg)
      .map((f) => ({
        flock_id: f.flock_id,
        date: data.date,
        qty_kg: parseFloat(f.feed_consumed_kg),
        formula_id: f.formula_id ? parseInt(f.formula_id) : undefined,
      }));

    if (feedEntries.length > 0) {
      try {
        const res = await feedLogService.bulkCreate(feedEntries);
        reportBulk(outcomes, "Feed Distribution", res,
          (n) => `${n} ${n === 1 ? "entry" : "entries"} recorded`,
          (i) => flockName(feedEntries[i]?.flock_id));
      } catch (err) {
        outcomes.push({ label: "Feed Distribution", status: "error", message: handleApiError(err) });
      }
    }

    // 4. Raw material stock-in (non-zero only) — BEFORE feed production.
    // Milling checks the silo and warns when it is short; with the day's
    // delivery saved after the batch, a supervisor who received 1,000 kg of
    // maize and milled 302 kg was told "need 302.5, have 0 … stock is now
    // -302.5" (live walk 2, W2-3). The balance came out right; the message
    // was false.
    const stockEntries = data.materials
      .filter((m) => parseFloat(m.qty) > 0)
      .map((m) => ({
        raw_material_id: m.raw_material_id,
        txn_type: "purchase" as const,
        date: data.date,
        qty: parseFloat(m.qty),
      }));

    if (stockEntries.length > 0) {
      try {
        const res = await stockService.bulkCreate(stockEntries);
        reportBulk(outcomes, "Raw Material Stock-In", res,
          (n) => `${n} ${n === 1 ? "material" : "materials"} received`,
          (i) => materialName(stockEntries[i]?.raw_material_id));
      } catch (err) {
        outcomes.push({ label: "Raw Material Stock-In", status: "error", message: handleApiError(err) });
      }
    }

    // 5. Feed production (one call per flock)
    const feedProdEntries = data.flocks.filter(
      (f) => parseFloat(f.feed_prepared_kg) > 0 && f.formula_id
    );

    for (const f of feedProdEntries) {
      try {
        const res = await feedProductionService.produce({
          formula_id: parseInt(f.formula_id),
          date: data.date,
          batch_size_kg: parseFloat(f.feed_prepared_kg),
        });
        // The batch saved either way; a short silo comes back as a warning
        // and must reach whoever filled in the report.
        outcomes.push(
          res.warnings.length > 0
            ? {
                label: `Feed Produced - ${f.batch_name}`,
                status: "warning",
                message: `${f.feed_prepared_kg} kg — ${res.warnings.join("; ")}`,
              }
            : { label: `Feed Produced - ${f.batch_name}`, status: "success", message: `${f.feed_prepared_kg} kg` },
        );
      } catch (err) {
        outcomes.push({ label: `Feed Produced - ${f.batch_name}`, status: "error", message: handleApiError(err) });
      }
    }

    // 6. Daily measurements.
    //
    // ONLY flocks with at least one reading are sent. Two reasons, and both
    // would be bugs if ignored: the server refuses a row carrying no measure
    // (so an untouched flock would come back as a red error on a report the
    // operator filled in correctly), and the write is an upsert assigning
    // every column (so an empty row for a flock that WAS measured this morning
    // would erase it).
    //
    // The corollary is a deliberate limitation: clearing every box on a flock
    // that has readings does nothing rather than deleting the day. Deleting is
    // owner/manager-only on the API and belongs on the Measurements page,
    // which has a delete button; silently making "save" a destructive verb
    // here would be worse than not offering it.
    // `measuresLoadFailed ? []` is belt and braces, and knowingly UNREACHABLE:
    // the step disables its inputs on a failed read, so no box can hold a value
    // and no test can tell this line from its absence. Kept so that removing
    // that `disabled` cannot turn a failed read into a deletion. See
    // docs/architecture/flock-metrics.md §8.
    const measuredFlocks = measuresLoadFailed ? [] : data.flocks.filter(hasAnyMeasure);
    if (measuredFlocks.length > 0) {
      const rows: SaveFlockMetrics[] = measuredFlocks.map((f) => ({
        flock_id: f.flock_id,
        date: data.date,
        // toMeasure, not Number: `Number("")` is 0, which would record a body
        // weight of zero grams for every box the operator left alone.
        avg_body_weight_g: toMeasure(f.m_body_weight_g),
        // Sent even for a brooder shed, where the form hides the box: hiding a
        // field is not clearing it, and an egg weight on a brooder-shed flock
        // is a reachable state. Prefilled, so this carries it back unchanged.
        avg_egg_weight_g: toMeasure(f.m_egg_weight_g),
        water_litres: toMeasure(f.m_water_litres),
        light_hours: toMeasure(f.m_light_hours),
        temp_min_c: toMeasure(f.m_temp_min_c),
        temp_max_c: toMeasure(f.m_temp_max_c),
        humidity_pct: toMeasure(f.m_humidity_pct),
        // Same rule, for the same reason: prefilled, not shown, carried back.
        notes: f.m_notes.trim() === "" ? null : f.m_notes,
      }));
      // NOTE for whoever wires offline sync: `device_id` is on this table and
      // the upsert assigns it from EXCLUDED too. No client sets it today, so
      // it is null everywhere and nulling it again is a no-op — but the moment
      // something starts writing it, this payload has to carry it as well.
      try {
        // reportBulk was extracted FROM this block — leaving it hand-rolled
        // would put two implementations of one rule in one file, which is the
        // drift the helper's own doc warns about.
        //
        // Partial failure is reported per flock, by name. "2 of 5 failed" on a
        // screen the operator is about to leave is not something they can act
        // on; "Shed B: body weight is recorded in GRAMS" is.
        const res = await flockMetricsService.bulkSave(rows);
        reportBulk(outcomes, "Measurements", res,
          (n) => `${n} ${n === 1 ? "flock" : "flocks"} recorded`,
          (i) => measuredFlocks[i]?.batch_name);
      } catch (err) {
        outcomes.push({ label: "Measurements", status: "error", message: handleApiError(err) });
      }
    }

    // 7. Sales (manager only — for everyone else, sales arrays stay empty)
    if (isManager) {
      await submitSales(outcomes);
    }

    setResults(outcomes);
    setSubmitted(true);
    setSubmitting(false);
  };

  const handleReset = () => {
    setStep(0);
    setSubmitted(false);
    setResults([]);
    setExistingCount(0);
    setMeasuresPrefilled(false);
    setMeasuresLoadFailed(false);
    setLogsLoadFailed(false);
    setDateError("");
    setData((prev) => ({
      ...prev,
      date: todayStr(),
      flocks: prev.flocks.map((f) => ({
        ...f,
        mortality: "0",
        mortality_cause: "",
        egg_trays: "",
        broken_trays: "0",
        feed_consumed_kg: "",
        feed_prepared_kg: "",
        formula_id: flocks.find((fl) => fl.id === f.flock_id)?.default_formula_id?.toString() || "",
        ...blankMeasures(),
        ...noPrior(),
      })),
      materials: prev.materials.map((m) => ({ ...m, qty: "" })),
      sales: emptySales(),
    }));
    // Re-trigger pre-fill for today so the operator sees any entries
    // already saved earlier in the day after they finish submitting.
    void handleDateChange(todayStr());
  };

  if (loading) {
    return (
      <>
        <PageMeta title="Daily Report | Poultry Book" description="Daily management report" />
        <PageBreadcrumb pageTitle="Daily Report" />
        <div className="py-20 text-center text-gray-500 dark:text-gray-400">Loading...</div>
      </>
    );
  }

  if (flocks.length === 0) {
    return (
      <>
        <PageMeta title="Daily Report | Poultry Book" description="Daily management report" />
        <PageBreadcrumb pageTitle="Daily Report" />
        <div className="mx-auto max-w-2xl card p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">No active flocks found. Please create flocks in Master Data first.</p>
        </div>
      </>
    );
  }

  // Success screen
  if (submitted) {
    const hasErrors = results.some((r) => r.status === "error");
    return (
      <>
        <PageMeta title="Daily Report | Poultry Book" description="Daily management report" />
        <PageBreadcrumb pageTitle="Daily Report" />
        <div className="mx-auto max-w-2xl card p-6 sm:p-8">
          <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white/90">
            {hasErrors ? "Submitted with Errors" : "Report Submitted"}
          </h3>
          <div className="space-y-2">
            {results.map((r, i) => (
              <div
                key={i}
                className={`flex items-center justify-between rounded-lg px-4 py-2.5 text-sm ${
                  r.status === "success"
                    ? "bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400"
                    : r.status === "warning"
                      ? "bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400"
                      : "bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400"
                }`}
              >
                <span className="font-medium">{r.label}</span>
                <span>{r.message}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex gap-3">
            <button
              onClick={handleReset}
              className="rounded-lg bg-gray-900 dark:bg-white px-4 py-2 text-sm font-medium text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100"
            >
              Submit Another Day
            </button>
            <a
              href="/"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Go to Dashboard
            </a>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageMeta title="Daily Report | Poultry Book" description="Daily management report" />
      <PageBreadcrumb pageTitle="Daily Report" />

      <div className="mx-auto max-w-2xl">
        {/* Stepper */}
        <div className="mb-6 flex items-center justify-between">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
                    i < step
                      ? "bg-success-500 text-white"
                      : i === step
                        ? "bg-brand-500 text-white"
                        : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                  }`}
                >
                  {i < step ? "✓" : i + 1}
                </div>
                <span className="mt-1 hidden text-[10px] text-gray-500 dark:text-gray-400 sm:block">
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`mx-1 h-0.5 w-6 sm:w-10 ${
                    i < step ? "bg-success-500" : "bg-gray-200 dark:bg-gray-700"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="card p-5 sm:p-8">
          {step === at(STEP_DATE) && (
            <StepDate
              date={data.date}
              onChange={handleDateChange}
              min={dateStrOffset(EDIT_WINDOW_DAYS - 1)}
              max={todayStr()}
              error={dateError}
              existingCount={existingCount}
              prefilling={prefilling}
              windowDays={EDIT_WINDOW_DAYS}
              logsLoadFailed={logsLoadFailed}
            />
          )}
          {step === at(STEP_FLOCK) && <StepFlockData flocks={data.flocks} onChange={updateFlock} />}
          {step === at(STEP_MEASUREMENTS) && (
            <StepMeasurements
              flocks={data.flocks}
              onChange={updateFlock}
              prefilled={measuresPrefilled}
              loadFailed={measuresLoadFailed}
            />
          )}
          {step === at(STEP_FEED) && (
            <StepFeedPrepared
              flocks={data.flocks}
              formulas={formulas}
              onChange={updateFlock}
              editMode={existingCount > 0}
            />
          )}
          {step === at(STEP_MATERIALS) && (
            <StepRawMaterials
              materials={data.materials}
              onChange={updateMaterial}
              editMode={existingCount > 0}
            />
          )}
          {step === SALES_STEP_INDEX && (
            <StepSales
              sales={data.sales}
              date={data.date}
              buyers={buyers}
              flocks={flocks}
              onAddEgg={addEgg}
              onUpdateEgg={updateEgg}
              onRemoveEgg={removeEgg}
              onAddBird={addBird}
              onUpdateBird={updateBird}
              onRemoveBird={removeBird}
              onAddManure={addManure}
              onUpdateManure={updateManure}
              onRemoveManure={removeManure}
              onAddBag={addBag}
              onUpdateBag={updateBag}
              onRemoveBag={removeBag}
            />
          )}
          {step === REVIEW_STEP_INDEX && (
            <StepReview
              data={data}
              formulas={formulas}
              isManager={isManager}
              measuresLoadFailed={measuresLoadFailed}
            />
          )}

          {/* Navigation */}
          <div className="mt-6 flex justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
            <button
              onClick={() => setStep((s) => s - 1)}
              disabled={step === 0}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Back
            </button>
            {step < REVIEW_STEP_INDEX ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canProceed()}
                className="rounded-lg bg-gray-900 dark:bg-white px-4 py-2 text-sm font-medium text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="rounded-lg bg-success-500 px-4 py-2 text-sm font-medium text-white hover:bg-success-600 disabled:opacity-40"
              >
                {submitting ? "Submitting..." : "Submit Report"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
